import { api } from './api';
import type { AuthUser } from './auth';

// ============================================================
// Auth
// ============================================================

export async function checkEmail(email: string) {
  const res = await api.post('/professional-auth/check-email', { email });
  return res.data as { status: boolean; available: boolean };
}

export async function signup(payload: {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  accept_terms: boolean;
  // Signups are invite-only now — this tokenized link is required.
  invite_token: string;
  mobile_number?: string;
  dial_code?: string;
}) {
  const res = await api.post('/professional-auth/signup', payload);
  return res.data as {
    status: boolean;
    message: string;
    accessToken: string;
    user: AuthUser;
  };
}

// ---- Apply to join (public application form) ----

export type PublicServiceCategory = {
  id: string;
  name: string;
  code?: string | null;
};

export async function getPublicServiceCategories() {
  const res = await api.get('/service-category/public');
  // The endpoint returns { status, data: [...] } — normalise to the array.
  const data = (res.data?.data ?? res.data?.info ?? res.data) as any;
  return (Array.isArray(data) ? data : []) as PublicServiceCategory[];
}

export async function submitProfessionalApplication(payload: {
  full_name: string;
  email: string;
  dial_code?: string;
  mobile_number: string;
  service_category_id?: string;
  accept_terms: boolean;
  cv: File;
}) {
  const form = new FormData();
  form.append('full_name', payload.full_name);
  form.append('email', payload.email);
  if (payload.dial_code) form.append('dial_code', payload.dial_code);
  form.append('mobile_number', payload.mobile_number);
  if (payload.service_category_id) {
    form.append('service_category_id', payload.service_category_id);
  }
  form.append('accept_terms', payload.accept_terms ? 'true' : 'false');
  form.append('cv', payload.cv, payload.cv.name);
  const res = await api.post('/professional-applications', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as { status: boolean; id: string; message: string };
}

// ---- Invite link (skip-application account creation) ----

export async function validateProInvite(token: string) {
  const res = await api.get(
    `/professional-applications/invite/${encodeURIComponent(token)}`,
  );
  return res.data as {
    status: boolean;
    valid: boolean;
    reason: string | null;
    email: string | null;
    // Prefill from the linked application (null for admin-created invites).
    full_name: string | null;
    dial_code: string | null;
    mobile_number: string | null;
  };
}

export async function verifyEmail(token: string) {
  const res = await api.post('/professional-auth/verify-email', { token });
  return res.data as
    | {
        status: true;
        alreadyVerified: false;
        accessToken: string;
        message: string;
        user: AuthUser;
      }
    | {
        status: true;
        alreadyVerified: true;
        accessToken: string;
        message: string;
        user: AuthUser;
      };
}

export async function resendVerification(email: string) {
  const res = await api.post('/professional-auth/resend-verification', {
    email,
  });
  return res.data as { status: boolean; message: string };
}

// Login is now a two-step flow: a correct password returns a short-lived
// `challenge_token` plus a flag telling the UI whether to ENROL (first
// login, `requires_2fa_setup`) or just VERIFY a code (`requires_2fa`). The
// real session token only comes back from `proTwofaVerify`.
export type ProLoginResponse =
  | {
      status: boolean;
      message: string;
      accessToken: string;
      user: AuthUser;
    }
  | {
      status: boolean;
      requires_2fa?: boolean;
      requires_2fa_setup?: boolean;
      challenge_token: string;
    };

export async function login(payload: { email: string; password: string }) {
  const res = await api.post('/professional-auth/login', payload);
  return res.data as ProLoginResponse;
}

// First-login enrolment: returns the QR (data-URL) + manual key to add the
// account to an authenticator app.
export async function proTwofaSetup(challengeToken: string) {
  const res = await api.post('/professional-auth/2fa/setup', {
    challenge_token: challengeToken,
  });
  return res.data as {
    status: boolean;
    secret: string;
    otpauth_url: string;
    qr_data_url: string;
  };
}

// Verify the 6-digit code — returns the real session token (and finalises
// enrolment on first login).
export async function proTwofaVerify(challengeToken: string, code: string) {
  const res = await api.post('/professional-auth/2fa/verify', {
    challenge_token: challengeToken,
    code,
  });
  return res.data as {
    status: boolean;
    message: string;
    accessToken: string;
    user: AuthUser;
  };
}

// Self-service "change authenticator" from account settings (authenticated):
// fetch a fresh QR, then confirm a code from the new device. No logout needed.
export async function proTwofaReenrollBegin() {
  const res = await api.post('/professional-auth/2fa/reenroll');
  return res.data as {
    status: boolean;
    secret: string;
    otpauth_url: string;
    qr_data_url: string;
  };
}

export async function proTwofaReenrollVerify(code: string) {
  const res = await api.post('/professional-auth/2fa/reenroll/verify', { code });
  return res.data as { status: boolean; message: string };
}

export async function logout() {
  try {
    await api.post('/professional-auth/logout');
  } catch {
    // Server is JWT-only, nothing to invalidate. Swallow any error
    // and clear local state regardless.
  }
}

export async function forgotSendOtp(email: string) {
  const res = await api.post('/professional-auth/forgot-password/send-otp', {
    email,
  });
  return res.data as {
    status: boolean;
    reference_number: string | null;
    message: string;
  };
}

export async function forgotVerifyOtp(payload: {
  reference_number: string;
  email: string;
  otp: string;
}) {
  const res = await api.post(
    '/professional-auth/forgot-password/verify-otp',
    payload,
  );
  return res.data as {
    status: boolean;
    remember_token: string;
    message: string;
  };
}

export async function forgotResetPassword(payload: {
  remember_token: string;
  new_password: string;
}) {
  const res = await api.put(
    '/professional-auth/forgot-password/reset-password',
    payload,
  );
  return res.data as { status: boolean; message: string };
}

export async function changeOwnPassword(payload: {
  current_password: string;
  new_password: string;
}) {
  const res = await api.put('/professional-auth/change-password', payload);
  return res.data as { status: boolean; message: string };
}

// Step 1 of authenticated email change — backend re-auths with the
// current password, then mails an OTP to the NEW address. Persist the
// returned `reference_number` for step 2.
export async function changeOwnEmailSendOtp(payload: {
  new_email: string;
  current_password: string;
}) {
  const res = await api.post(
    '/professional-auth/change-email/send-otp',
    payload,
  );
  return res.data as {
    status: boolean;
    reference_number: string;
    message: string;
  };
}

// Step 2 — submit the OTP the pro received at `new_email`. On success
// the backend has swapped the login email and marked it verified.
export async function changeOwnEmailVerify(payload: {
  reference_number: string;
  new_email: string;
  otp: string;
}) {
  const res = await api.post(
    '/professional-auth/change-email/verify',
    payload,
  );
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Profile
// ============================================================

export async function getMe() {
  const res = await api.get('/professional/me');
  return res.data as { status: boolean; info: any };
}

/**
 * Slim variant of `/professional/me`. Pass the sections you actually render,
 * e.g. `getMeSlim(['services','addons'])`, and the server skips the weekly
 * availability rows + the portfolio's big presigned URLs. Omitting the arg
 * behaves exactly like `getMe()`.
 */
export async function getMeSlim(
  include: Array<'services' | 'addons' | 'availabilities' | 'portfolio'>,
) {
  const res = await api.get('/professional/me', {
    params: { include: include.join(',') },
  });
  return res.data as { status: boolean; info: any };
}

/** Lean payload for the Services page — no portfolio, no availability. */
export async function getMyServices() {
  const res = await api.get('/professional/me/services');
  return res.data as {
    status: boolean;
    info: {
      professional_id: string;
      minimum_booking_value: number | null;
      services: any[];
      service_addons: any[];
    };
  };
}

/** The pro's gallery on its own (presigned read URLs). */
export async function getMyPortfolio() {
  const res = await api.get('/professional/me/portfolio');
  return res.data as { status: boolean; data: any[] };
}

export type MyPendingProfileChange = {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  // The proposed (not-yet-live) edit. May carry `services`,
  // `service_addons`, `pending_treatments`, `pending_addons`, bio, rates…
  payload: Record<string, any>;
  changed_fields: string[];
  submitted_at: string;
};

/**
 * The pro's outstanding profile-change request (everything except
 * availability is held for admin review). `data` is null when nothing is
 * pending. The pro UI uses this to show an "awaiting approval" banner and
 * to reflect the proposed selection — otherwise a just-submitted service
 * shows as OFF (the live profile still has the old set) and the pro thinks
 * it never saved.
 */
export async function getMyPendingChange() {
  const res = await api.get('/professional/me/profile-change');
  return res.data as { status: boolean; data: MyPendingProfileChange | null };
}

export async function updateMe(patch: Record<string, any>) {
  const res = await api.put('/professional/me', patch);
  return res.data as {
    status: boolean;
    message: string;
    // Set when a live pro's edit (identity / listing change) was held for
    // admin review rather than applied immediately.
    pending_review?: boolean;
    // Set alongside pending_review when SOME edits (pure pricing) went live
    // in the same save while others were held.
    partial_applied?: boolean;
  };
}

/**
 * Save the pro's onboarding-wizard position (1..10) so they resume where they
 * left off. Lightweight + not vetting-gated. Fire-and-forget from the wizard —
 * a failed save must never block the pro from advancing.
 */
export async function saveOnboardingStep(step: number) {
  const res = await api.put('/professional/me/onboarding-step', { step });
  return res.data as { status: boolean; onboarding_step: number };
}

// ============================================================
// Tax (HMRC UTR). The value is write-only from the pro side — we only ever
// read back a presence flag, never the number itself.
// ============================================================

/** Whether the pro has a UTR on file (drives the banner + payout-setup field). */
export async function getProTaxUtrStatus() {
  const res = await api.get('/professional/me/tax-utr');
  return res.data as {
    status: boolean;
    has_tax_utr: boolean;
    added_at: string | null;
  };
}

/** Save / update the pro's UTR (10 digits; encrypted server-side). */
export async function setProTaxUtr(utr: string) {
  const res = await api.put('/professional/me/tax-utr', { utr });
  return res.data as {
    status: boolean;
    has_tax_utr: boolean;
    message: string;
  };
}

// ============================================================
// Pro-authored add-ons (the pro's own, self-priced add-ons shown on
// their public profile for Hair / Beauty & Wellness).
// ============================================================

export type ProAddon = {
  id: string;
  name: string;
  price: number;
  duration_minutes: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export type ProAddonInput = {
  name: string;
  price: number;
  duration_minutes: number;
  status?: 'ACTIVE' | 'INACTIVE';
};

export async function listMyAddons() {
  const res = await api.get('/professional/me/addons');
  return res.data as { status: boolean; data: ProAddon[] };
}

export async function createMyAddon(input: ProAddonInput) {
  const res = await api.post('/professional/me/addons', input);
  return res.data as {
    status: boolean;
    id?: string;
    message: string;
    pending_review?: boolean;
  };
}

export async function updateMyAddon(id: string, input: ProAddonInput) {
  const res = await api.put(`/professional/me/addons/${id}`, input);
  return res.data as {
    status: boolean;
    message: string;
    pending_review?: boolean;
  };
}

export async function deleteMyAddon(id: string) {
  const res = await api.delete(`/professional/me/addons/${id}`);
  return res.data as {
    status: boolean;
    message: string;
    pending_review?: boolean;
  };
}

// ============================================================
// Pro-managed main treatments (the pro's own treatments shown on their
// public profile for Hair / Beauty & Wellness). For a live (APPROVED) pro
// every change is held for admin review (see Profile Changes); pros still
// onboarding apply them directly.
// ============================================================

export type ProTreatment = {
  id: string;
  name: string;
  price: number;
  duration_minutes: number;
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING_APPROVAL';
  pricing_unit: 'PER_HOUR' | 'PER_BOOKING';
  service_category_id: string | null;
  category_name: string | null;
  requires_patch_test?: boolean;
};

export type ProTreatmentInput = {
  service_category_id: string;
  name: string;
  price: number;
  duration_minutes: number;
  requires_patch_test?: boolean;
};

export async function listMyTreatments() {
  const res = await api.get('/professional/me/treatments');
  return res.data as { status: boolean; data: ProTreatment[] };
}

export async function createMyTreatment(input: ProTreatmentInput) {
  const res = await api.post('/professional/me/treatments', input);
  return res.data as {
    status: boolean;
    id?: string;
    message: string;
    pending_review?: boolean;
  };
}

export async function updateMyTreatment(
  id: string,
  input: Partial<ProTreatmentInput>,
) {
  const res = await api.put(`/professional/me/treatments/${id}`, input);
  return res.data as {
    status: boolean;
    message: string;
    pending_review?: boolean;
  };
}

export async function deleteMyTreatment(id: string) {
  const res = await api.delete(`/professional/me/treatments/${id}`);
  return res.data as {
    status: boolean;
    message: string;
    pending_review?: boolean;
  };
}

/** GDPR data export — a JSON copy of the pro's own data. */
export async function exportMyData() {
  const res = await api.get('/professional/me/data-export');
  return res.data as { status: boolean; info: any; generated_at: string };
}

/**
 * GDPR right to erasure — permanently deletes + anonymises the pro's
 * account. Irreversible.
 */
export async function deleteMyAccount() {
  const res = await api.delete('/professional/me');
  return res.data as { status: boolean; message: string };
}

/**
 * Propose a service that isn't in the catalogue. Returns the newly
 * created PENDING_APPROVAL service (already linked to this pro). It's
 * not customer-bookable until an admin approves it.
 */
export async function proposeCustomService(input: {
  service_category_id: string;
  name: string;
}) {
  const res = await api.post('/professional/me/services/custom', input);
  return res.data as {
    status: boolean;
    info: {
      id: string;
      name: string;
      status: string;
      service_category_id: string;
    };
    message: string;
  };
}

// ============================================================
// Documents
// ============================================================

export type DocumentRequirement = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  accepts: string[];
  has_front_back: boolean;
  has_expiry: boolean;
  is_required: boolean;
  sort_order: number;
};

export type ProfessionalDocument = {
  id: string;
  front_file_url: string | null;
  back_file_url: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  original_filename: string | null;
  expiry_date: string | null;
  review_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  review_notes: string | null;
  uploaded_at: string | null;
  requirement: { id: string };
};

export async function getDocumentCatalog() {
  const res = await api.get('/document-requirement/catalog');
  return res.data as { status: boolean; data: DocumentRequirement[] };
}

export async function getMyDocuments() {
  const res = await api.get('/professional/me/documents');
  return res.data as { status: boolean; data: ProfessionalDocument[] };
}

export async function uploadMyDocument(payload: {
  requirement_id: string;
  front_file_url: string;
  back_file_url?: string | null;
  mime_type?: string | null;
  file_size_bytes?: number | null;
  original_filename?: string | null;
  expiry_date?: string | null;
}) {
  const res = await api.post('/professional/me/documents', payload);
  return res.data as { status: boolean; info: ProfessionalDocument; message: string };
}

// ============================================================
// File upload (presigned PUT → S3)
// ============================================================

export type UploadPurpose =
  | 'PROFESSIONAL_DOCUMENT'
  | 'PROFESSIONAL_DBS'
  | 'PROFESSIONAL_INSURANCE'
  | 'PROFESSIONAL_VIDEO'
  | 'PROFESSIONAL_PORTFOLIO'
  | 'ADMIN_AVATAR'
  | 'GENERIC';

/**
 * Upload a file from a signed-in pro. Bytes go website → API → S3:
 * the API proxies the multipart upload and returns the resulting S3
 * URL. The frontend never PUTs to S3 directly, which sidesteps the
 * CORS / signature edge cases the presigned-URL variant ran into.
 *
 * `/uploads/me` is the user-scoped twin of the admin-only `/uploads`
 * route; both live on the same controller.
 */
export async function uploadFile(file: File, purpose: UploadPurpose) {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('purpose', purpose);
  const res = await api.post('/uploads/me', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as { status: boolean; key: string; url: string };
}

// ============================================================
// CMS / static pages
// ============================================================

export type CmsPage = {
  title: string;
  code: string;
  content: string;
  updated_at: string;
};

// ============================================================
// Time blocks (holidays / unavailability ranges)
// ============================================================

export type ProTimeBlockStatus = 'ACTIVE' | 'CANCELLED';

export type ProTimeBlock = {
  id: string;
  from_date: string; // YYYY-MM-DD
  to_date: string;
  // Optional partial-day window. Both null = full-day block.
  start_time: string | null; // HH:MM:SS
  end_time: string | null;
  reason: string | null;
  status: ProTimeBlockStatus;
  created_at: string;
  updated_at: string;
};

export async function listMyTimeBlocks(includeCancelled = false) {
  const res = await api.get('/professional/me/time-blocks', {
    params: includeCancelled ? { include_cancelled: 'true' } : {},
  });
  return res.data as {
    status: boolean;
    data: ProTimeBlock[];
    message?: string;
  };
}

export async function createMyTimeBlock(payload: {
  from_date: string;
  to_date: string;
  reason?: string | null;
  // Provide both for a partial-day (hourly) block; omit both for a
  // full-day block.
  start_time?: string | null;
  end_time?: string | null;
}) {
  const res = await api.post('/professional/me/time-blocks', payload);
  return res.data as { status: boolean; id: string; message: string };
}

export async function cancelMyTimeBlock(id: string) {
  const res = await api.delete(`/professional/me/time-blocks/${id}`);
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Notification inbox (shared shape with customer; kept on the pro
// axios instance so JWT user_type discrimination remains correct).
// ============================================================

export type InboxNotificationStatus = 'PENDING' | 'SENT' | 'READ' | 'FAILED';

export type InboxNotification = {
  id: string;
  status: InboxNotificationStatus;
  sent_at: string | null;
  read_at: string | null;
  notification: {
    id: string;
    title: string;
    body: string;
    action: string | null;
    action_id: string | null;
    image_url: string | null;
    sent_at: string | null;
  };
};

export async function listMyNotifications(opts?: {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
}) {
  const res = await api.get('/me/notifications', {
    params: {
      limit: opts?.limit ?? 20,
      offset: opts?.offset ?? 0,
      unread_only: opts?.unread_only ? 'true' : undefined,
    },
  });
  return res.data as {
    status: boolean;
    data: InboxNotification[];
    total_count: number;
    unread_count: number;
  };
}

export async function unreadNotificationCount() {
  const res = await api.get('/me/notifications/unread-count');
  return res.data as { status: boolean; unread_count: number };
}

export async function markNotificationRead(id: string) {
  const res = await api.put(`/me/notifications/${id}/read`);
  return res.data as { status: boolean; message: string };
}

export async function markAllNotificationsRead() {
  const res = await api.put('/me/notifications/read-all');
  return res.data as { status: boolean; message: string };
}

export async function deleteNotification(id: string) {
  const res = await api.delete(`/me/notifications/${id}`);
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Active sessions (Security page) — shared shape with customer.
// ============================================================

export type UserSessionRow = {
  id: string;
  device_label: string | null;
  os: string | null;
  browser: string | null;
  is_mobile: boolean;
  ip_address: string | null;
  city: string | null;
  country: string | null;
  last_activity_at: string | null;
  created_at: string;
  is_current: boolean;
};

export async function listMySessions() {
  const res = await api.get('/me/sessions');
  return res.data as {
    status: boolean;
    current_session_id: string | null;
    data: UserSessionRow[];
  };
}

export async function revokeSession(id: string) {
  const res = await api.delete(`/me/sessions/${id}`);
  return res.data as { status: boolean; message: string };
}

export async function revokeAllOtherSessions() {
  const res = await api.delete('/me/sessions/others');
  return res.data as {
    status: boolean;
    revoked_count: number;
    message: string;
  };
}

// ============================================================
// Pro bookings — list assignments, start/complete/cancel.
// ============================================================

export type ProBookingRow = {
  id: string;
  reference: string;
  scheduled_start: string;
  duration_minutes: number;
  status: string;
  source: 'ONE_OFF' | 'SUBSCRIPTION';
  total_amount: number;
  currency: string;
  address_line1: string;
  city: string | null;
  // Populated only when status is CANCELLED_BY_*. `cancelled_by`
  // tells us who initiated, `refund_outcome` records what the
  // customer got back. PARTIAL_CARD_REFUND is the late-cancel middle
  // window (50% charged, 50% refunded to card).
  cancelled_at?: string | null;
  cancelled_by?: 'CUSTOMER' | 'PROFESSIONAL' | 'ADMIN' | 'SYSTEM' | null;
  cancellation_reason?: string | null;
  refund_outcome?:
    | 'NONE'
    | 'WALLET_CREDIT'
    | 'CARD_REFUND'
    | 'PARTIAL_CARD_REFUND'
    | 'PARTIAL_WALLET_CREDIT'
    | null;
  refund_amount?: number | null;
  customer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  service: { id: string; name: string } | null;
  service_category: { id: string; name: string } | null;
  created_at?: string;
};

export async function listProBookings(opts?: {
  limit?: number;
  offset?: number;
  status?: string;
  q?: string;
  service_id?: string;
  date_from?: string;
  date_to?: string;
}) {
  const res = await api.get('/professional/me/bookings', {
    params: {
      limit: opts?.limit ?? 10,
      offset: opts?.offset ?? 0,
      ...(opts?.status ? { status: opts.status } : {}),
      ...(opts?.q ? { q: opts.q } : {}),
      ...(opts?.service_id ? { service_id: opts.service_id } : {}),
      ...(opts?.date_from ? { date_from: opts.date_from } : {}),
      ...(opts?.date_to ? { date_to: opts.date_to } : {}),
    },
  });
  return res.data as {
    status: boolean;
    data: ProBookingRow[];
    total_count: number;
  };
}

export async function getProBookingDetail(id: string) {
  const res = await api.get(`/professional/me/bookings/info/${id}`);
  return res.data as { status: boolean; info: any };
}

export async function startProBooking(id: string) {
  const res = await api.put(`/professional/me/bookings/${id}/start`);
  return res.data as { status: boolean; message: string };
}

export async function completeProBooking(id: string, note?: string) {
  const res = await api.put(`/professional/me/bookings/${id}/complete`, {
    note: note ?? null,
  });
  return res.data as { status: boolean; message: string };
}

export async function cancelProBooking(id: string, reason?: string) {
  const res = await api.put(`/professional/me/bookings/${id}/cancel`, {
    reason: reason ?? null,
  });
  return res.data as {
    status: boolean;
    booking_status: string;
    refund_outcome: string;
    refund_amount: number;
    message: string;
  };
}

// Accept a booking that's sitting PENDING_PROFESSIONAL_CONFIRM. Flips
// the booking to CONFIRMED and notifies the customer.
export async function acceptProBooking(id: string) {
  const res = await api.put(`/professional/me/bookings/${id}/accept`);
  return res.data as {
    status: boolean;
    booking_status: string;
    message: string;
  };
}

// Reject a PENDING_PROFESSIONAL_CONFIRM booking. Full refund to the
// customer; the booking ends on CANCELLED_BY_PRO → REFUNDED.
export async function rejectProBooking(id: string, reason: string | null) {
  const res = await api.put(`/professional/me/bookings/${id}/reject`, {
    reason: reason ?? null,
  });
  return res.data as {
    status: boolean;
    booking_status: string;
    refund_outcome?: string;
    refund_amount?: number;
    message: string;
  };
}

// Approve a customer's reschedule request → booking CONFIRMED at the
// new time. Distinct from acceptProBooking (fresh booking).
export async function approveReschedule(id: string) {
  const res = await api.put(
    `/professional/me/bookings/${id}/reschedule/approve`,
  );
  return res.data as {
    status: boolean;
    booking_status: string;
    message: string;
  };
}

// Decline a reschedule request → the booking REVERTS to its original
// time and stays CONFIRMED (no cancellation, no refund).
export async function declineReschedule(id: string, reason: string | null) {
  const res = await api.put(
    `/professional/me/bookings/${id}/reschedule/decline`,
    { reason: reason ?? null },
  );
  return res.data as {
    status: boolean;
    booking_status: string;
    message: string;
  };
}

// Decline ≠ cancel: customer keeps their booking + payment; only the
// pro is detached. Admin then reassigns. Reason text is mandatory in
// the UI but the backend allows null.
export async function declineProBooking(id: string, reason: string | null) {
  const res = await api.put(`/professional/me/bookings/${id}/decline`, {
    reason: reason ?? null,
  });
  return res.data as {
    status: boolean;
    booking_status: string;
    message: string;
  };
}

// ============================================================
// Pro payouts — Stripe Connect onboarding + payout history.
// ============================================================

export type ProConnectStatus = {
  stripe_account_status:
    | 'NOT_STARTED'
    | 'PENDING'
    | 'ACTIVE'
    | 'RESTRICTED'
    | 'REJECTED';
  payouts_enabled: boolean;
  charges_enabled: boolean;
  onboarding_complete: boolean;
  // Derived gate the dashboard banner + booking-accept check both
  // read. True only when payouts + charges enabled, KYC submitted,
  // and no disabled_reason flagged by Stripe.
  payouts_ready: boolean;
  currently_due: string[];
  past_due: string[];
  disabled_reason: string | null;
};

export async function getProConnectStatus() {
  const res = await api.get('/professional/me/stripe-connect/status');
  return res.data as { status: boolean; info: ProConnectStatus };
}

export async function createProConnectOnboardingLink() {
  const res = await api.post(
    '/professional/me/stripe-connect/onboarding-link',
  );
  return res.data as {
    status: boolean;
    url: string;
    expires_at: number;
  };
}

export async function createProConnectDashboardLink() {
  const res = await api.post(
    '/professional/me/stripe-connect/dashboard-link',
  );
  return res.data as { status: boolean; url: string };
}

// `account_update`-style link for an already-onboarded pro to change
// their bank details or resolve a freshly-flagged requirement
// without re-running the whole KYC flow.
export async function createProConnectUpdateLink() {
  const res = await api.post(
    '/professional/me/stripe-connect/update-link',
  );
  return res.data as {
    status: boolean;
    url: string;
    expires_at: number | null;
  };
}

// Called from /professional/setup-payouts/return on mount. Stamps the
// TOS acceptance audit row server-side and returns the latest account
// snapshot so the return screen can pick the right "success" or
// "still outstanding" UI.
export async function markProConnectReturn() {
  const res = await api.post(
    '/professional/me/stripe-connect/return',
  );
  return res.data as {
    status: boolean;
    info: ProConnectStatus;
  };
}

export type ProPayoutRow = {
  id: string;
  gross_amount: number;
  platform_fee_percentage: number;
  platform_fee_amount: number;
  net_amount: number;
  currency: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REVERSED' | 'SKIPPED';
  // Truthful, pro-facing status derived by the backend. `status` = PAID only
  // means the money reached the pro's Stripe balance; display_* distinguishes
  // transferred / in-transit / actually-in-bank. Optional so the page still
  // works against an older API build that doesn't send them yet.
  display_status?:
    | 'PENDING'
    | 'PROCESSING'
    | 'IN_TRANSIT'
    | 'DEPOSITED'
    | 'FAILED'
    | 'ON_HOLD'
    | 'REVERSED'
    | string;
  display_label?: string;
  stripe_transfer_id: string | null;
  bank_paid_at?: string | null;
  bank_payout_initiated_at?: string | null;
  error_message: string | null;
  transferred_at: string | null;
  failed_at: string | null;
  retry_count: number;
  created_at: string;
  booking: { id: string; reference: string; scheduled_start: string } | null;
};

export async function listProPayouts(opts?: {
  limit?: number;
  offset?: number;
}) {
  const res = await api.get('/professional/me/payouts', {
    params: { limit: opts?.limit ?? 20, offset: opts?.offset ?? 0 },
  });
  return res.data as {
    status: boolean;
    data: ProPayoutRow[];
    total_count: number;
    totals_by_status: Record<string, number>;
  };
}

// ============================================================
// Pro reviews — read-only list of reviews left for the pro.
// ============================================================

export type ProReviewRow = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  customer: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  service: { id: string; name: string } | null;
  booking: { id: string; reference: string } | null;
};

export async function listProReviews(opts?: {
  limit?: number;
  offset?: number;
}) {
  const res = await api.get('/professional/me/reviews', {
    params: { limit: opts?.limit ?? 20, offset: opts?.offset ?? 0 },
  });
  return res.data as {
    status: boolean;
    data: ProReviewRow[];
    total_count: number;
  };
}

// ============================================================
// Per-client agreed rates (Q4) — a pro sets a custom rate for a
// specific repeat customer + service. The pricing core prefers an
// active rate here over the pro's global custom_price / the catalog
// base_price.
// ============================================================

export type ProClient = {
  customer_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

export type ProClientRate = {
  id: string;
  custom_rate: number | null;
  customer_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  service_id: string;
  service_name: string;
  created_at: string;
  updated_at: string;
};

// Distinct customers this pro has booked with — populates the
// "pick a client" dropdown on the rate form.
export async function listMyClients() {
  const res = await api.get('/professional/customer-rates/clients');
  return res.data as {
    status: boolean;
    data: ProClient[];
    message?: string;
  };
}

// One distinct (client, service) booking pair. The rate form derives both
// autocompletes from these and cross-filters them.
export type ProClientServicePair = {
  customer_id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  service_id: string;
  service_name: string;
};

export async function listMyClientServicePairs() {
  const res = await api.get('/professional/customer-rates/client-services');
  return res.data as {
    status: boolean;
    data: ProClientServicePair[];
    message?: string;
  };
}

// The pro's existing active agreed rates, with client + service names.
export async function listMyClientRates() {
  const res = await api.get('/professional/customer-rates');
  return res.data as {
    status: boolean;
    data: ProClientRate[];
    message?: string;
  };
}

// Upsert on (customer, service): update if a rate exists, else create.
export async function upsertClientRate(payload: {
  customer_id: string;
  service_id: string;
  custom_rate: number;
}) {
  const res = await api.post('/professional/customer-rates', payload);
  return res.data as { status: boolean; id: string; message: string };
}

export async function deleteClientRate(id: string) {
  const res = await api.delete(`/professional/customer-rates/${id}`);
  return res.data as { status: boolean; message: string };
}

export type CmsUserType = 'CUSTOMER' | 'PROFESSIONAL';

export async function getCmsPage(
  code: string,
  userType: CmsUserType = 'PROFESSIONAL',
) {
  const res = await api.get(`/cms/public/${code}`, {
    params: { user_type: userType },
  });
  return res.data as { status: boolean; data: CmsPage };
}
